import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { getSessionCountry } from '/@/store/session/selectors';
import FieldviewAutofillIcon from './FieldviewAutofillIcon';

interface ProductSelectorProps {
    crop: string;
    value: number | undefined | string;
    onChange: (value: number | undefined | string) => void;
    productsQuery: any;
    disabled: boolean;
    units: UnitConverter;
    minEnvironment: number;
    maxEnvironment: number;
    environment?: number;
    error?: string;
    isFromFieldview?: boolean;
}

const ProductSelector = ({
    crop,
    value,
    onChange,
    productsQuery,
    disabled,
    units,
    minEnvironment,
    maxEnvironment,
    environment,
    error,
    isFromFieldview,
}: ProductSelectorProps) => {
    const country = useSelector(getSessionCountry);

    const filteredProducts = useMemo(() => {
        if (!productsQuery.data) return [];
        if (country?.code === 'BR') {
            const products = [...productsQuery.data.featuredProducts, ...productsQuery.data.otherProducts].filter(
                (p: any) => p.leafCounterCoefficients?.length
            );
            return products;
        }
        if (['AR', 'UY'].includes(country?.code)) {
            const products = [...productsQuery.data.featuredProducts, ...productsQuery.data.otherProducts];
            return products.concat({ id: -1, product: { catalogName: _('Híbrido Genérico') } });
        }
        return [];
    }, [country?.code, productsQuery.data]);

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {_('Producto sembrado')}
            </Typography>
            {crop === 'corn' ? (
                <MuiAutocomplete
                    data-testid='field-monitoring-creation-dialog-product-selector'
                    disableClearable
                    placeholder={_('Indicá el híbrido')}
                    sx={{
                        background: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            top: '-10px',
                        },
                    }}
                    disabled={
                        productsQuery.isValidating ||
                        disabled ||
                        (environment !== undefined &&
                            (units.toYield(environment, 'corn', 2).value <
                                units.toYield(minEnvironment, 'corn', 2).value ||
                                units.toYield(environment, 'corn', 2).value >
                                    units.toYield(maxEnvironment, 'corn', 2).value))
                    }
                    value={value}
                    onChange={(_: any, option: any) => {
                        onChange(option?.id);
                    }}
                    options={filteredProducts}
                    loading={productsQuery.isValidating}
                    getOptionLabel={(option: any) => option.product?.catalogName || ''}
                    getOptionValue={(option: any) => option.id}
                    error={!!error}
                    helperText={error}
                    endAdornment={isFromFieldview ? <FieldviewAutofillIcon /> : undefined}
                />
            ) : (
                <TextField
                    placeholder={_('Ingresá el nombre de la semilla')}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value ? String(e.target.value) : undefined)}
                    fullWidth
                    error={!!error}
                    helperText={error}
                    InputProps={
                        isFromFieldview
                            ? {
                                  endAdornment: (
                                      <InputAdornment position='end'>
                                          <FieldviewAutofillIcon />
                                      </InputAdornment>
                                  ),
                              }
                            : undefined
                    }
                />
            )}
        </Stack>
    );
};

export default ProductSelector;
